import { Injectable } from '@angular/core';
import { CaeBridge, CaeConnection, CaeItem, CaeRail } from '@project/cae/cae-data.service';
import { ArticleCreationManagerFactory } from '@project/components/article/manager/article-creation.manager.factory';
import { ArticleCreationManager } from '@project/components/article/manager/article-creation.manager';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { CaeImportHelperService } from './cae-import-helper.service';
import { ArticleConnectionPropertyValue } from '@article/constants/article-connection-property-values';
import { Connection } from '@3d-scene/model3d/connection';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ConnectionLevel } from '@project/cae/util/connection-level';
import { ConnectionLevelUtils } from '@project/cae/util/connection-level-utils';
import { PropertyUtils } from '@article/util/property-utils';
import { ConnectionAreaVO } from '@shared/vo/meta-element.vo';
import { ArticleConnectionPropertyDef } from '@article/constants/article-connection-property-def';
import { ConnectionCubeSearchRequirements } from '@article/components/connections/connection-creation/search/connection-cube-search-requirements';
import { lastValueFrom, Observable } from 'rxjs';
import { Log } from '@shared/debug/log';
import { PropertyCreator } from '@article/util/property-creator';
import { AccessoryService, ConnectionSearchResult } from '@article/service/accessory.service';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { CaeImportErrorCollector } from '../cae-import-error-collector';
import { PropertyValueObject, PropertyValueVO } from '@project/components/article/model/vo/property.vo';
import { CaeRailsData } from './cae-rails-data';
import { JumperService } from '@project/accessory/jumper.service';
import { CaeProjectHelper } from '@project/processor/cae/cae-project-helper';
import { AddArticleError } from '@project/components/article/processor/add-article-error';

class CaeConnectionInfo {

  constructor(
    public connection: CaeConnection,
    public item: CaeItem,
    public articleContainer: ArticleContainer[],
  ) {
  }

  public includes(articleContainers: ArticleContainer[]): boolean {
    return articleContainers.every((container: ArticleContainer) => this.articleContainer.includes(container));
  }
}

/** A bridge together with the currently imported rail it belongs to. */
interface CaeRailBridge {
  rail: CaeRail;
  bridge: CaeBridge;
}

@Injectable({
  providedIn: 'root'
})
export class CaeImportBridgesService {

  private caeImportErrorCollector!: CaeImportErrorCollector;
  private articleCreationManager!: ArticleCreationManager;

  constructor(
    private readonly articleCreationManagerFactory: ArticleCreationManagerFactory,
    private readonly caeImportHelperService: CaeImportHelperService,
    private readonly propertyCreator: PropertyCreator,
    private readonly accessoryService: AccessoryService,
    private readonly containerService: ContainerService,
    private readonly jumperService: JumperService,
    private readonly caeProjectHelper: CaeProjectHelper,
  ) { }

  private getBridgesForRails(caeRailsData: CaeRailsData): CaeRailBridge[] {
    const railBridges: CaeRailBridge[] = [];
    for (const railOfBridges of caeRailsData.allRails) {
      for (const caeBridge of railOfBridges.bridges) {
        const railBridge = this.createBridgeForCurrentRails(caeBridge, railOfBridges, caeRailsData);
        if (railBridge) {
          railBridges.push(railBridge);
        }
      }
    }
    return railBridges;
  }

  private createBridgeForCurrentRails(caeBridge: CaeBridge, railOfBridge: CaeRail, caeRailsData: CaeRailsData): CaeRailBridge | undefined {
    const connections: CaeConnection[] = [];
    let currentRailOfBridge: CaeRail | undefined;

    for (const caeConnection of caeBridge.connections) {
      const currentRail = caeRailsData.currentRails.find((rail) => rail.items.some((item) => item.id === caeConnection.itemId));
      if (currentRail) {
        // Connection is on current rail -> include
        currentRailOfBridge ??= currentRail;
        connections.push(caeConnection);
      } else if (caeRailsData.allRails.some((rail) => rail.items.some((item) => item.id === caeConnection.itemId))) {
        // Connection will be on another rail -> exclude
      } else {
        // Connection is not in the imported data -> include
        connections.push(caeConnection);
      }
    }

    // A bridge belongs to the current rails only if at least one of its connections points to an item on them.
    // If none of its connections can be resolved at all, it is handled while its own rail is imported.
    // Otherwise bridges of other rails (and their errors) would be assigned to the currently imported rail.
    const rail = currentRailOfBridge ?? caeRailsData.currentRails.find((currentRail) => currentRail.id === railOfBridge.id);
    if (!rail || connections.length === 0) {
      return undefined;
    }

    return { rail: rail, bridge: { ...caeBridge, connections: connections } };
  }

  public async addBridges(caeRailsData: CaeRailsData, caeImportErrorCollector: CaeImportErrorCollector): Promise<void> {
    this.caeImportErrorCollector = caeImportErrorCollector;
    this.articleCreationManager = this.articleCreationManagerFactory.getNewInstance();
    this.articleCreationManager.showFaultMessage = false;
    const railBridges = this.getBridgesForRails(caeRailsData);
    for (const railBridge of railBridges) {
      this.articleCreationManager.clean();
      // report errors for the rail the bridge actually belongs to
      this.caeImportErrorCollector.processRail(railBridge.rail);
      if (railBridge.bridge.type === 'Saddle') {
        await this.addJumper(railBridge.bridge, caeRailsData);
      } else {
        await this.addWire(railBridge.bridge, caeRailsData);
      }
    }
  }

  private async addJumper(caeBridge: CaeBridge, caeRailsData: CaeRailsData) {

    const matchingItems: CaeConnectionInfo[] = this.findMatchingItems(caeRailsData.currentRails, caeBridge.connections);
    if (matchingItems.length !== caeBridge.connections.length) {
      this.caeImportErrorCollector.addErrorForBridge(caeBridge, 'caeImport.jumper.error.article_on_position_missing');
    }

    try {

      // First add the jumpers defined as accessory
      await this.addJumpersFromAccessory(caeBridge, matchingItems, caeRailsData);

      // Then fill rest of the bridge with jumper service
      await this.addMissingJumpers(caeBridge, matchingItems);

    } catch (error) {
      if (error instanceof AddArticleError) {
        this.caeImportErrorCollector.addErrorForBridge(caeBridge, error.message);
      } else {
        this.caeImportErrorCollector.addErrorForBridge(caeBridge, 'global.message.unexpected_error');
      }

    }
  }

  private async addMissingJumpers(caeBridge: CaeBridge, matchingItems: CaeConnectionInfo[]) {
    const itemIds = matchingItems.flatMap((matchingItem) => matchingItem.articleContainer.map((articleContainer) => articleContainer.articleGUID));
    const success = await this.jumperService.addJumpersToArticles(itemIds, caeBridge.connections[0].level ?? 1);
    if (!success) {
      this.caeImportErrorCollector.addErrorForBridge(caeBridge, 'caeImport.jumper.error.articles_could_not_be_fully_bridged');
    }
  }

  private async addJumpersFromAccessory(caeBridge: CaeBridge, matchingItems: CaeConnectionInfo[], caeRailsData: CaeRailsData): Promise<void> {
    for (let i = 0; i < matchingItems.length; i++) {
      const connection = matchingItems[i].connection;
      const caeItem = caeRailsData.currentRails.flatMap((caeRail) => caeRail.items).find((caeItem) => caeItem.id === connection.itemId);
      if (!caeItem) {
        this.caeImportErrorCollector.addErrorForBridge(caeBridge, 'caeImport.jumper.error.article_on_position_missing');
      }
      for (const caeItemAccessory of caeItem?.items ?? []) {
        const article = this.caeProjectHelper.getArticleByCaeBaseArticle(caeItemAccessory);
        if (article && ArticleUtils.isBridge(article)) {
          const givingAreas = ArticleUtils.givingAreas(article);
          const articleGuids = matchingItems.slice(i, i + givingAreas.length).flatMap((matchingItem) => matchingItem.articleContainer.map((articleContainer) => articleContainer.articleGUID));
          const success = await this.jumperService.addJumperArticleToArticles(articleGuids, article, connection.level ?? 1);
          if (success) {
            // -2 because the loop should target the lax index of the added bridge in the next iteration
            i += Math.max(0, givingAreas.length - 2);
          } else {
            this.caeImportErrorCollector.addErrorForBridge(caeBridge, 'caeImport.jumper.error.could_not_add_accessory', [article.articleNo]);
          }
          break;
        }
      }
    }
  }

  private async addWire(caeBridge: CaeBridge, caeRailsData: CaeRailsData) {
    const connectionType = this.getConnectionType(caeBridge);
    const matchingItems: CaeConnectionInfo[] = this.findMatchingItems(caeRailsData.currentRails, caeBridge.connections);
    if (matchingItems.length !== caeBridge.connections.length) {
      this.caeImportErrorCollector.addErrorForBridge(caeBridge, 'caeImport.jumper.error.article_on_position_missing');
    }

    const acceptingCubes: Connection[] = this.getFilteredAcceptingCubesByArticles(matchingItems, connectionType);
    if (acceptingCubes.length === 0) {
      this.caeImportErrorCollector.addErrorForBridge(caeBridge, 'caeImport.jumper.error.suitable_connection_not_found');
    }

    try {
      const searchResult = await lastValueFrom(this.loadSuitableBridge(acceptingCubes, connectionType, caeBridge.crossSection, caeBridge.color));
      this.prioritizeSearchResultWithSpecifiedAccessories(searchResult, matchingItems);
      await this.connectionsByRequirementsLoadedSuccess(searchResult, caeBridge, matchingItems);
    } catch (error) {
      Log.error('Could not load articles by requirements: ', error);
      await this.connectionsByRequirementsLoadedFailure(caeBridge);
    }
  }

  private findMatchingItems(caeRails: CaeRail[], caeConnections: CaeConnection[]): CaeConnectionInfo[] {
    const matchingItems: CaeConnectionInfo[] = [];
    for (const caeConnection of caeConnections) {
      const matchingCaeItems = caeRails.flatMap((caeRail) => caeRail.items).filter((caeItem) => caeItem.id === caeConnection.itemId);
      if (matchingCaeItems.length === 1) {
        const matchingCaeItem = matchingCaeItems[0];
        // only base article-part of complex article should be considered in regard to connection of jumpers
        const articleContainers = ArticleContainerUtils.getBaseArticleContainers(this.caeImportHelperService.getArticleContainersForProjectArticle(matchingCaeItem));
        matchingItems.push(new CaeConnectionInfo(caeConnection, matchingCaeItem, articleContainers));
      }
    }
    return matchingItems;
  }

  private getConnectionType(caeBridge: CaeBridge) {
    if(caeBridge.type === 'Saddle') {
      return ArticleConnectionPropertyValue.BRIDGE_SHAFT_CONNECTION_TYPE;
    } else {
      return ArticleConnectionPropertyValue.Wire_CONNECTION_TYPE;
    }
  }

  private getFilteredAcceptingCubesByArticles(caeConnectionInfos: CaeConnectionInfo[], connectionType: string): Connection[] {
    let filteredAcceptingCubes: Connection[] = [];
    for (const caeConnectionInfo of caeConnectionInfos) {
      for (const articleContainer of caeConnectionInfo.articleContainer) {
        let acceptingCubesByContainer: Connection[] = this.getAcceptingCubesByLevel(articleContainer, caeConnectionInfo.connection.level ?? 1);
        acceptingCubesByContainer.sort((c1, c2) => c1.globalRectangle.center.z - c2.globalRectangle.center.z);
        acceptingCubesByContainer = this.filterConnectionsByType(acceptingCubesByContainer, connectionType);
        acceptingCubesByContainer = ConnectionInfoUtils.filterAcceptingCubes(acceptingCubesByContainer, true);
        if (acceptingCubesByContainer.length === 0) {
          return [];
        }
        filteredAcceptingCubes = filteredAcceptingCubes.concat(acceptingCubesByContainer);
      }
    }
    return filteredAcceptingCubes;
  }

  private getAcceptingCubesByLevel(articleContainer: ArticleContainer, usedLevels: number): Connection[] {
    // consider levelNr-properties only for base parts of complex article, otherwise use geometric position to determine levels
    const connectionLevels: ConnectionLevel[] = ConnectionLevelUtils.createConnectionLevelsByProperties(articleContainer);

    let filteredAcceptingCubes: Connection[] = [];
    const connectionLevel: ConnectionLevel = ConnectionLevelUtils.findConnectionLevel(usedLevels, connectionLevels);
    if (connectionLevel) {
      filteredAcceptingCubes = filteredAcceptingCubes.concat(connectionLevel.getFilteredAcceptingCubes());
    }
    return filteredAcceptingCubes;
  }

  private filterConnectionsByType(connections: Connection[], connectionTypes: string): Connection[] {
    let wireConAdded = false;
    const result = [];
    for (const con of connections) {
      const type = PropertyUtils.getStringPropertyValue(
        (con.metaElementVO as ConnectionAreaVO).connectionRequirements,
        ArticleConnectionPropertyDef.TYPE,
      );
      if (connectionTypes.includes(type)) {
        if (type === ArticleConnectionPropertyValue.Wire_CONNECTION_TYPE) {
          if (!wireConAdded) {
            result.push(con);
            wireConAdded = true;
          }
        } else {
          result.push(con);
        }
      }
    }

    return result;
  }

  private loadSuitableBridge(acceptingCubes: Connection[],
                             connectionType: string,
                             crossSection: number | undefined,
                             color: string | undefined): Observable<ConnectionSearchResult> {

    const cubesSearchRequirements: ConnectionCubeSearchRequirements[] =
      this.propertyCreator.createCubesSearchRequirementsByDefault(acceptingCubes);

    return this.accessoryService.getBridgeFavoriteArticlesByRequirements(cubesSearchRequirements, undefined, connectionType, crossSection, color);
  }

  /** We want the jumper articles specified in the referenced items accessories to be preferred
   * when searching for a suitable jumper, so we put those first in the searchResults array */
  private prioritizeSearchResultWithSpecifiedAccessories(searchResult: ConnectionSearchResult, caeConnectionInfos: CaeConnectionInfo[]): void {
    const accessoryArticleNumbers = caeConnectionInfos
      .flatMap(connectionWrapper => connectionWrapper.item.items
        .map(accessory => accessory.articleNo));

    searchResult.vo.connections.sort((a, b) =>
      Number(accessoryArticleNumbers.includes(b.article.articleNo)) - Number(accessoryArticleNumbers.includes(a.article.articleNo)),
    );
  }

  private async connectionsByRequirementsLoadedFailure(caeBridge: CaeBridge): Promise<void> {
    this.caeImportErrorCollector.addErrorForBridge(caeBridge, 'caeImport.jumper.error.could_not_load_articles_by_requriments', []);
  }

  private async connectionsByRequirementsLoadedSuccess(connectionSearchResult: ConnectionSearchResult, caeBridge: CaeBridge, caeConnectionInfos: CaeConnectionInfo[]): Promise<void> {
    if (!await this.tryAddBridgeArticleToProject(connectionSearchResult, caeBridge, caeConnectionInfos)) {
      this.caeImportErrorCollector.addErrorForBridge(caeBridge, 'caeImport.jumper.error.suitable_connection_not_found');
    }
  }

  private async tryAddBridgeArticleToProject(connectionSearchResult: ConnectionSearchResult, caeBridge: CaeBridge, caeConnectionInfos: CaeConnectionInfo[]): Promise<boolean> {
    for (const connection of connectionSearchResult.vo.connections) {
      for (const connectionSet of connection.connectionSets) {
        if (
          !ArticleUtils.isBridge(connection.article) ||
          connectionSet.connectionNames.length === caeBridge.connections.length
        ) {
          connection.connectionSets = [ connectionSet ];

          try {
            await this.articleCreationManager.createArticlesBySuitableConnections(connection, connectionSearchResult.connectionCubeVectors, false);
            await this.articleInserted(caeBridge, caeConnectionInfos);
            return true;
          } catch (error) {
            Log.error('Could not insert article by connections: ', error);
            return false;
          }
        }
      }
    }
    return false;
  }

  private async articleInserted(caeBridge: CaeBridge, caeConnectionInfos: CaeConnectionInfo[]): Promise<void> {
    const createdContainers: ArticleContainer[] = this.articleCreationManager.getCreatedArticleContainers();
    if (createdContainers.length > 0) {
      const containerToProcess: ArticleContainer = createdContainers[0];
      this.setCableLengthIfAvailable(caeBridge, containerToProcess);
      this.setWireTypeIfAvailable(caeBridge, containerToProcess);
      this.setCableMetadataIfAvailable(createdContainers, caeConnectionInfos);
      this.setOtherSideIfNecessary(caeBridge, createdContainers[0], caeConnectionInfos);
    }
  }

  private setCableMetadataIfAvailable(createdContainers: ArticleContainer[], caeConnectionInfos: CaeConnectionInfo[]) {
    for (const createdContainer of createdContainers) {
      const acceptingArticleContainers = this.containerService.findParentAcceptingArticleContainers(createdContainer);
      if (acceptingArticleContainers.length === 0) {
        continue;
      }
      const connection = caeConnectionInfos.find((wrapper) => wrapper.includes(acceptingArticleContainers))?.connection;
      if (connection) {
        if (connection.connectionPoint) {
          PropertyUtils.setStringPropertyValue(
            createdContainer.articleUsage.infoProperties,
            ArticleUsageInfoPropertyDef.CAE_BRIDGE_CONNECTION_POINT,
            connection.connectionPoint,
          );
        }
        if (connection.termination) {
          PropertyUtils.setStringPropertyValue(
            createdContainer.articleUsage.infoProperties,
            ArticleUsageInfoPropertyDef.CAE_BRIDGE_TERMINATION,
            connection.termination,
          );
        }
      }
    }
  }

  private setCableLengthIfAvailable(caeBridge: CaeBridge, containerToProcess: ArticleContainer) {
    if (caeBridge.length) {
      PropertyUtils.setIntPropertyValue(
        containerToProcess.articleUsage.infoProperties,
        ArticleUsageInfoPropertyDef.CABLE_LENGTH,
        caeBridge.length,
      );
    }
  }

  private setWireTypeIfAvailable(caeBridge: CaeBridge, containerToProcess: ArticleContainer) {
    if (caeBridge.wireType) {
      PropertyUtils.setStringPropertyValue(
        containerToProcess.articleUsage.infoProperties,
        ArticleUsageInfoPropertyDef.CAE_WIRE_TYPE,
        caeBridge.wireType,
      );
    }
  }

  private setOtherSideIfNecessary(caeBridge: CaeBridge, articleContainer: ArticleContainer, caeConnectionInfos: CaeConnectionInfo[]) {
    for (const caeConnection of caeBridge.connections) {
      if (!caeConnectionInfos.some((caeConnectionInfo) => caeConnection.itemId === caeConnectionInfo.connection.itemId)) {
        const valueObject: PropertyValueObject = {type: 'CaeConnection', ...caeConnection};
        PropertyUtils.addPropertyValues(articleContainer.articleUsage.infoProperties, ArticleUsageInfoPropertyDef.CAE_BRIDGE_OTHER_SIDE, [ PropertyValueVO.createObjectValue(valueObject) ]);
      }
    }
  }
}
